import React, { forwardRef, useImperativeHandle } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Box,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_WALLET_BALANCE_HISTORY } from "../graphql/wallet-history";
import {
  WalletBalanceHistory,
  BalanceChangeType,
} from "../types/wallet-history";

interface WalletBalanceHistoryProps {
  walletId: string;
}

const getChangeTypeColor = (changeType: BalanceChangeType) => {
  switch (changeType) {
    case BalanceChangeType.DEPOSIT:
    case BalanceChangeType.TRADE_EXECUTION:
      return "success";
    case BalanceChangeType.WITHDRAWAL:
    case BalanceChangeType.FEE:
      return "error";
    case BalanceChangeType.POSITION_CLOSE:
      return "warning";
    default:
      return "default";
  }
};

const getChangeTypeLabel = (changeType: BalanceChangeType) => {
  switch (changeType) {
    case BalanceChangeType.TRADE_EXECUTION:
      return "Trade Execution";
    case BalanceChangeType.POSITION_CLOSE:
      return "Position Close";
    case BalanceChangeType.MANUAL_ADJUSTMENT:
      return "Manual Adjustment";
    case BalanceChangeType.DEPOSIT:
      return "Deposit";
    case BalanceChangeType.WITHDRAWAL:
      return "Withdrawal";
    case BalanceChangeType.FEE:
      return "Fee";
    case BalanceChangeType.INTEREST:
      return "Interest";
    default:
      return changeType;
  }
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const formatDate = (timestamp: string) => {
  return new Date(timestamp).toLocaleString();
};

const WalletBalanceHistoryComponent = forwardRef<
  any,
  WalletBalanceHistoryProps
>(({ walletId }, ref) => {
  const { loading, error, data, refetch } = useQuery(
    GET_WALLET_BALANCE_HISTORY,
    {
      variables: { walletId, limit: 50 },
      fetchPolicy: "cache-and-network",
    },
  );

  useImperativeHandle(ref, () => ({
    refetch,
  }));

  if (loading) {
    return (
      <Box p={2}>
        <Typography>Loading balance history...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography color="error">
          Error loading balance history: {error.message}
        </Typography>
      </Box>
    );
  }

  const balanceHistory = data?.walletBalanceHistory || [];

  if (balanceHistory.length === 0) {
    return (
      <Box p={2}>
        <Typography>No balance history available for this wallet.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Balance History
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Previous Balance</TableCell>
              <TableCell>Change</TableCell>
              <TableCell>New Balance</TableCell>
              <TableCell>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {balanceHistory.map((history: WalletBalanceHistory) => (
              <TableRow key={history.id}>
                <TableCell>{formatDate(history.timestamp)}</TableCell>
                <TableCell>
                  <Chip
                    label={getChangeTypeLabel(history.changeType)}
                    color={getChangeTypeColor(history.changeType) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>{formatCurrency(history.previousBalance)}</TableCell>
                <TableCell
                  sx={{
                    color:
                      history.changeAmount >= 0 ? "success.main" : "error.main",
                    fontWeight: "bold",
                  }}
                >
                  {history.changeAmount >= 0 ? "+" : ""}
                  {formatCurrency(history.changeAmount)}
                </TableCell>
                <TableCell>{formatCurrency(history.newBalance)}</TableCell>
                <TableCell>{history.reason || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
});

export { WalletBalanceHistoryComponent };
